<div class="shadow-xs layout-topbar flex items-center justify-between h-16 px-3 sm:px-4">
    <!-- ===== Logo + Menu Toggle ===== -->
    <!-- self-stretch so the switcher inside can hang its panel off the BAR's
         bottom edge rather than its own small trigger's. Everything in here is
         still centred by items-center, so nothing moves. -->
    <div class="flex items-center gap-2 min-w-0 self-stretch">
        @if (showMenuIcon) {
            <button
                class="layout-menu-button layout-topbar-action w-9 h-9 flex-shrink-0 flex items-center justify-center rounded-xl hover:bg-[var(--surface-hover)] transition-all duration-200 focus:outline-none"
                (click)="layoutService.onMenuToggle()"
            >
                <i class="pi pi-bars text-lg"></i>
            </button>
        }
        <a class="flex items-center gap-2 flex-shrink-0" routerLink="/">
            @if (loggedInUser?.tenant) {
                <img
                    [src]="loggedInUser?.tenant?.logoUrl || '/images/logo/default-logo.png'"
                    class="h-8 w-auto object-contain"
                    [alt]="loggedInUser?.tenant?.businessName"
                />
            } @else {
                <img
                    src="/images/logo/logo.png"
                    class="h-8 w-auto object-contain"
                    alt="Default Logo"
                />
            }
        </a>

        @if (loggedInUser && !isTTUserAccount) {
            <app-tenant-switcher class="self-stretch" [loggedInUser]="loggedInUser" />
        }
        @if (!loggedInUser?.tenant?.id) {
            <h6
                class="hidden xl:block text-sm font-semibold text-[var(--text-color)] truncate max-w-[160px]"
            >
                {{ loggedInUser?.tenant?.businessName }}
            </h6>
        }
    </div>

    <!-- ===== Desktop Actions (lg+) ===== -->
    <div class="hidden lg:flex items-center gap-4 xl:gap-6 flex-shrink-0 pr-2">
        <!-- Store chip -->
        @if (loggedInUser?.tenant?.websiteDomainUrl || loggedInUser?.tenant?.platformDomainUrl) {
            <div
                class="flex items-center gap-2 py-1.5 px-1.5 rounded-xl bg-[var(--surface-card)] border border-[var(--surface-border)]"
            >
                <div
                    class="w-7 h-7 xl:w-8 xl:h-8 rounded-lg flex items-center justify-center bg-green-500/10 text-green-500 flex-shrink-0"
                >
                    <i class="pi pi-globe text-sm"></i>
                </div>
                <div class="flex flex-col leading-none gap-0.5">
                    <span
                        class="text-[9px] xl:text-[10px] uppercase tracking-wider text-[var(--text-color-secondary)]"
                    >
                        Store
                    </span>
                    <button
                        type="button"
                        (click)="onVisitTenantWebsite()"
                        class="text-xs xl:text-sm font-semibold hover:underline text-left leading-tight max-w-[100px] xl:max-w-[140px] truncate"
                    >
                        {{
                            loggedInUser?.tenant?.websiteDomainUrl ||
                                loggedInUser?.tenant?.platformDomainUrl
                        }}
                    </button>
                </div>
            </div>
        }

        <!-- Theme controls -->
        <div
            class="flex items-center gap-1 xl:gap-2 py-1.5 px-1.5 rounded-xl bg-[var(--surface-card)] border border-[var(--surface-border)]"
        >
            <button
                type="button"
                (click)="onToggleTheme()"
                pTooltip="{{ themeTooltip }}"
                tooltipPosition="bottom"
                [attr.aria-label]="themeTooltip"
                class="w-8 h-8 xl:w-9 xl:h-9 rounded-lg flex items-center justify-center transition-all duration-200 hover:scale-110"
                [ngClass]="
                    layoutService.isDarkTheme()
                        ? 'bg-violet-500/10 text-violet-400'
                        : 'bg-yellow-400/10 text-yellow-500'
                "
            >
                <i [class]="themeIcon + ' text-sm xl:text-base'"></i>
            </button>

            <div class="w-px h-5 xl:h-6 bg-[var(--surface-border)]"></div>

            <button
                type="button"
                (click)="layoutService.toggleConfigPanel(); $event.stopPropagation()"
                pTooltip="Theme Settings"
                tooltipPosition="bottom"
                aria-label="Open theme configurator"
                class="w-8 h-8 xl:w-9 xl:h-9 rounded-lg flex items-center justify-center text-[var(--text-color-secondary)] hover:text-[var(--text-color)] transition-all duration-200 hover:scale-110"
            >
                <i class="pi pi-palette text-sm xl:text-base"></i>
            </button>
        </div>

        <!-- User dropdown -->
        <div class="relative">
            <button
                type="button"
                data-testid="header-user-menu-trigger"
                (click)="isOpen = !isOpen"
                (keyup.enter)="isOpen = !isOpen"
                [attr.aria-expanded]="isOpen"
                tabindex="0"
                aria-haspopup="true"
                class="flex items-center gap-2 py-1.5 px-1.5 rounded-xl border border-[var(--surface-border)] bg-[var(--surface-card)] hover:bg-[var(--surface-hover)] transition-all duration-200 cursor-pointer"
            >
                <!-- Avatar -->
                <div class="relative flex-shrink-0">
                    @if (loggedInUser?.profileImageUrl) {
                        <img
                            [src]="loggedInUser?.profileImageUrl"
                            [alt]="loggedInUser?.firstName"
                            class="w-7 h-7 xl:w-8 xl:h-8 rounded-lg object-cover"
                        />
                    } @else {
                        <div
                            class="w-7 h-7 xl:w-9 xl:h-9 rounded-lg flex items-center justify-center text-[var(--text-color-secondary)]"
                            [attr.aria-label]="loggedInUser?.firstName || 'User avatar'"
                        >
                            <i class="pi pi-user text-sm xl:text-base"></i>
                        </div>
                    }
                    <div
                        class="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-green-500 border-2 border-[var(--surface-card)]"
                    ></div>
                </div>

                <!-- Name + Role (xl+) -->
                <div class="hidden xl:flex flex-col text-left leading-none">
                    <span class="text-sm font-semibold text-[var(--text-color)]">
                        {{ loggedInUser?.firstName }} {{ loggedInUser?.lastName }}
                    </span>
                    <span class="text-xs text-[var(--text-color-secondary)] mt-0.5">
                        {{ loggedInUser?.userRole?.value }}
                    </span>
                </div>

                <i
                    class="pi pi-angle-down text-xs text-[var(--text-color-secondary)] transition-transform duration-300"
                    [ngClass]="{ 'rotate-180': isOpen }"
                ></i>
            </button>

            <!-- Dropdown menu -->
            @if (isOpen) {
                <div
                    data-testid="header-user-menu"
                    class="absolute right-0 mt-2 w-56 rounded-xl overflow-hidden bg-[var(--surface-card)] border border-[var(--surface-border)] shadow-xl z-50"
                >
                    @if (loggedInUser?.tenant?.id) {
                        <button
                            type="button"
                            (click)="manageAccount()"
                            class="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-[var(--text-color)] hover:bg-[var(--surface-hover)] transition-colors duration-150 cursor-pointer"
                        >
                            <i
                                class="pi pi-user-edit text-sm text-[var(--text-color-secondary)]"
                            ></i>
                            <span>Manage Account</span>
                        </button>
                    }

                    <!-- Only a PLATFORM account leaves a store: this switches
                         back to Seldit, which a merchant holds no membership
                         for and would be refused with a 404. userType is the
                         whole check - the server derives accountType from it,
                         so ttUser already means a management account. -->
                    @if (loggedInUser?.tenant?.id && isTTUserAccount) {
                        <button
                            type="button"
                            (click)="exitTenant()"
                            class="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-[var(--text-color)] hover:bg-[var(--surface-hover)] transition-colors duration-150 cursor-pointer"
                        >
                            <i
                                class="pi pi-times-circle text-sm text-[var(--text-color-secondary)]"
                            ></i>
                            <span>Exit Tenant</span>
                        </button>
                    }
                    @if (!loggedInUser?.tenant?.id) {
                        <button
                            type="button"
                            (click)="manageProfile()"
                            class="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-[var(--text-color)] hover:bg-[var(--surface-hover)] transition-colors duration-150 cursor-pointer"
                        >
                            <i
                                class="pi pi-user-edit text-sm text-[var(--text-color-secondary)]"
                            ></i>
                            <span>Manage Profile</span>
                        </button>
                    }

                    <div class="border-t border-[var(--surface-border)] mx-2"></div>

                    <button
                        type="button"
                        data-testid="header-logout-button"
                        (click)="logout()"
                        class="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-red-500 hover:bg-red-500/10 transition-colors duration-150 cursor-pointer"
                    >
                        <i class="pi pi-sign-out text-sm"></i>
                        <span>Logout</span>
                    </button>
                </div>
            }
        </div>
    </div>

    <!-- ===== Mobile Actions (below lg) ===== -->
    <div class="flex lg:hidden items-center gap-2 flex-shrink-0">
        <!-- Quick theme toggle (mobile) -->
        <button
            type="button"
            (click)="onToggleTheme()"
            class="w-9 h-9 flex items-center justify-center rounded-xl hover:bg-[var(--surface-hover)] transition-all duration-200"
            [ngClass]="layoutService.isDarkTheme() ? 'text-violet-400' : 'text-yellow-500'"
        >
            <i [class]="themeIcon + ' text-base'"></i>
        </button>

        <!-- Ellipsis menu -->
        <div class="relative">
            <button
                type="button"
                data-testid="header-mobile-menu-trigger"
                (click)="isMenuOpen = !isMenuOpen"
                class="w-9 h-9 flex items-center justify-center rounded-xl hover:bg-[var(--surface-hover)] transition-all duration-200"
            >
                <i class="pi pi-ellipsis-v text-base"></i>
            </button>

            @if (isMenuOpen) {
                <div
                    data-testid="header-mobile-menu"
                    class="absolute right-0 mt-2 w-72 bg-[var(--surface-card)] border border-[var(--surface-border)] rounded-2xl shadow-2xl p-3 z-50 space-y-2"
                >
                    <!-- Theme row -->
                    <div class="flex items-center gap-2">
                        <div
                            class="flex items-center justify-between flex-1 px-3 h-10 rounded-lg bg-[var(--surface-section)]"
                        >
                            <div class="flex items-center gap-2 text-sm text-[var(--text-color)]">
                                <i [class]="themeIcon + ' text-sm'"></i>
                                <span>{{ layoutService.isDarkTheme() ? 'Dark' : 'Light' }}</span>
                            </div>
                            <button
                                type="button"
                                (click)="onToggleTheme()"
                                class="relative w-10 h-5 rounded-full transition-colors duration-300 focus:outline-none"
                                [ngClass]="
                                    layoutService.isDarkTheme() ? 'bg-indigo-600' : 'bg-gray-300'
                                "
                            >
                                <span
                                    class="absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform duration-300"
                                    [ngClass]="
                                        layoutService.isDarkTheme()
                                            ? 'translate-x-5'
                                            : 'translate-x-0'
                                    "
                                ></span>
                            </button>
                        </div>

                        <button
                            type="button"
                            (click)="layoutService.toggleConfigPanel(); $event.stopPropagation()"
                            class="w-10 h-10 flex items-center justify-center rounded-lg bg-[var(--surface-section)] text-[var(--text-color-secondary)] hover:text-[var(--text-color)] transition-all duration-200"
                            aria-label="Theme Settings"
                        >
                            <i class="pi pi-palette text-sm"></i>
                        </button>
                    </div>

                    <!-- User section -->
                    <div class="border-t border-[var(--surface-border)] pt-2">
                        <button
                            type="button"
                            data-testid="header-mobile-user-menu-trigger"
                            (click)="isUserOpen = !isUserOpen"
                            class="flex items-center w-full px-3 h-11 rounded-lg hover:bg-[var(--surface-hover)] transition-colors duration-150 gap-3"
                        >
                            <img
                                [src]="loggedInUser?.profileImageUrl || 'default.jpg'"
                                [alt]="loggedInUser?.firstName"
                                class="w-8 h-8 rounded-lg object-cover flex-shrink-0"
                            />
                            <div class="flex flex-col text-left leading-none min-w-0">
                                <span
                                    class="text-sm font-semibold text-[var(--text-color)] truncate"
                                >
                                    {{ loggedInUser?.firstName }} {{ loggedInUser?.lastName }}
                                </span>
                                <span class="text-xs text-[var(--text-color-secondary)] mt-0.5">
                                    {{ loggedInUser?.userRole?.value }}
                                </span>
                            </div>
                            <i
                                class="pi ml-auto text-xs text-[var(--text-color-secondary)] flex-shrink-0"
                                [ngClass]="isUserOpen ? 'pi-angle-up' : 'pi-angle-down'"
                            >
                            </i>
                        </button>

                        @if (isUserOpen) {
                            <div class="mt-1 space-y-0.5" data-testid="header-mobile-user-menu">
                                @if (loggedInUser?.tenant?.id) {
                                    <button
                                        (click)="manageAccount()"
                                        class="flex items-center gap-3 w-full px-4 py-2.5 text-sm rounded-lg text-[var(--text-color)] hover:bg-[var(--surface-hover)] transition-colors duration-150"
                                    >
                                        <i
                                            class="pi pi-user-edit text-sm text-[var(--text-color-secondary)]"
                                        ></i>
                                        Manage Account
                                    </button>
                                }
                                @if (loggedInUser?.tenant?.id && isTTUserAccount) {
                                    <button
                                        (click)="exitTenant()"
                                        class="flex items-center gap-3 w-full px-4 py-2.5 text-sm rounded-lg text-[var(--text-color)] hover:bg-[var(--surface-hover)] transition-colors duration-150"
                                    >
                                        <i
                                            class="pi pi-times-circle text-sm text-[var(--text-color-secondary)]"
                                        ></i>
                                        Exit Tenant
                                    </button>
                                }
                                @if (!loggedInUser?.tenant?.id) {
                                    <button
                                        (click)="manageProfile()"
                                        class="flex items-center gap-3 w-full px-4 py-2.5 text-sm rounded-lg text-[var(--text-color)] hover:bg-[var(--surface-hover)] transition-colors duration-150"
                                    >
                                        <i
                                            class="pi pi-user-edit text-sm text-[var(--text-color-secondary)]"
                                        ></i>
                                        Manage Profile
                                    </button>
                                }
                                <button
                                    type="button"
                                    data-testid="header-mobile-logout-button"
                                    (click)="logout()"
                                    class="flex items-center gap-3 w-full px-4 py-2.5 text-sm rounded-lg text-red-500 hover:bg-red-500/10 transition-colors duration-150"
                                >
                                    <i class="pi pi-sign-out text-sm"></i>
                                    Logout
                                </button>
                            </div>
                        }
                    </div>
                </div>
            }
        </div>
    </div>
</div>

<app-config-panel></app-config-panel>
<app-confirmation-modal
    #confirmationModal
    [title]="confirmationTitle"
    [message]="confirmationMessage"
    (confirmed)="onConfirmed($event)"
></app-confirmation-modal>
